import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import {
  Table,
  Flex,
  Avatar,
  Badge,
  Text,
  Button,
  calculateUserColor
} from "@theorgchartdev/storybook";
import React, { useEffect, useState } from "react";
import cx from "classnames";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { appstr } from "@coreapp/utils/appstr";
import { enumPlanRoles, kUserPlanRoles } from "@coreapp/constants";

const UserLabel = props => {
  let data = props.rowState.data;
  let displayName = data.name;
  if (data.displayName) displayName = data.displayName;
  let avatarLabel = displayName ? displayName[0].toUpperCase() : "";
  let avatarColor = calculateUserColor(avatarLabel);
  let isActive = data?.active;
  let avatarStyle = {};
  let textColor = null;
  if (!isActive) {
    avatarStyle.opacity = "0.3";
    textColor = "lightgrey";
  }
  return (
    <Flex gap={"8px"} alignItems={"center"} padding={"4px"} className={"oc-name-column"}>
      <Badge
        border
        color="success"
        maxLength={3}
        overlap="circular"
        pin="top-right"
        showBadge={isActive === true}
        size={"small"}
      >
        <Avatar
          text={avatarLabel}
          isTriggerButton={false}
          color={avatarColor}
          style={avatarStyle}
          size={"small"}
        />
      </Badge>
      <Text size={"xsmall"} modifier={"medium"} colorModifier={textColor}>
        {displayName}
      </Text>
    </Flex>
  );
};

export const SharedUsersTable = () => {
  const { chartDocument } = useChartDocumentContext();
  const { wsMgr } = useWSMgrContext();
  const {
    shareContextValue: { setShareData, openShareModal }
  } = useWSMgrContext();
  const {
    planManager,
    manageUsersContextValue: { onSavePlanUsers }
  } = usePlanManagerContext();

  const [usersData, setSharedUsersData] = useState({ data: [] });
  const planMgr = planManager.state;

  const onManageAccess = () => {
    let plan = planMgr?.GetPlanInfo();
    setShareData({
      usersWithAccess: plan["users"],
      owner: plan["owner"],
      name: plan["name"],
      docId: plan["ID"],
      emailInvite: plan["emailInvite"],
      defaultRole: plan["defaultRole"]
    });
    openShareModal(onSavePlanUsers);
  };

  const RebuildSharedUsersData = () => {
    let newTableData = {
      data: BuildTableData()
    };
    setSharedUsersData(newTableData);
  };

  const BuildTableData = () => {
    let planInfo = planMgr?.GetPlanInfo();
    if (!planMgr || !planInfo) return [];

    let sharedUsers = planInfo["users"];

    var usersClone = sharedUsers.slice(0);

    // sort by active
    usersClone.sort(function (x, y) {
      return x["role"] === enumPlanRoles.owner && x["isActive"]
        ? -1
        : y["role"] === enumPlanRoles.owner && y["isActive"]
          ? 1
          : 0;
    });

    // filter out invalid users or disabled users
    usersClone = usersClone.filter(user => kUserPlanRoles.indexOf(user["role"]) > -1);

    return usersClone.map(aUser => {
      let normalizedUser = { name: "", role: "", current: "", target: "" };
      let userID = aUser["name"];
      let displayName = userID;
      if (aUser["displayName"]) displayName = aUser["displayName"];
      normalizedUser.displayName = displayName;
      normalizedUser.name = userID;
      normalizedUser.role = appstr(aUser["role"]);
      normalizedUser.active = aUser["isActive"];
      return normalizedUser;
    });
  };

  useEffect(() => {
    RebuildSharedUsersData();
  }, [planManager, chartDocument, wsMgr]);

  const valueColumnOptions = {
    columnStyle: { color: "#3d3d4a" }
  };

  let columns = [
    {
      field: "displayName",
      headerName: appstr("User"),
      type: "cellRenderer",
      columnWidth: "seventy-five",
      CellRenderer: UserLabel
    },
    {
      field: "role",
      headerName: appstr("Role"),
      columnWidth: "twenty-five",
      columnOptions: valueColumnOptions
    }
  ];

  const gridOptions = {
    // set background colour on every row, this is probably bad, should be using CSS classes
    // set background colour on even rows again, this looks bad, should be using CSS classes
    getRowStyle: (data, rowIndex, isHovered) => {
      if (rowIndex === 0) {
        return { fontSize: "12px" };
      }
    }
  };

  const { canShare } = getPlanPermissions({ useCurrentPlan: true });

  return (
    <Flex direction={"column"}>
      <Flex className={"oc-dashboard-header"}>
        <Text size={"small"} modifier={"medium"}>
          {appstr("Users with access")}
        </Text>
        {canShare && (
          <span>
            <Button onClick={onManageAccess} modifier={"secondary"} size={"small"}>
              {appstr("Manage access")}
            </Button>
          </span>
        )}
      </Flex>
      <div className={cx("oc-metrics-table", "ocn-list-scrollbar")}>
        <Table
          gridOptions={gridOptions}
          columns={columns}
          data={usersData.data}
          showHeader={true}
          containerPadding={false}
          sortEnabled={true}
          rowSize={"mediumR"}
          textSize={"smallT"}
          customHeaderClass={"oc-table-header-display-small"}
          customTableClass={"oc-table-display-small"}
        />
      </div>
    </Flex>
  );
};
